import React, { useMemo, useState } from 'react';
import { FolderTree, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { categoriesAPI } from '../services/api';
import { childrenOf, topSubcategories, parentOf } from '../services/catalogPath';

// Guided migration: pick level-2 sub-categories of one category and group them under a parent (existing or new)
const GroupSubcategoriesBlock = ({ categories, onApplied }) => {
  const [catSlug, setCatSlug] = useState(categories[0]?.slug || '');
  const [selected, setSelected] = useState([]);
  const [parent, setParent] = useState('');
  const [newParent, setNewParent] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const cat = categories.find(c => c.slug === catSlug);
  // candidates: level-2 nodes without children (a parent cannot become a child — 3 levels max)
  const candidates = useMemo(() => topSubcategories(cat).filter(s => !childrenOf(cat, s).length), [cat]);
  const parentChoices = topSubcategories(cat).filter(s => !selected.includes(s));
  const parentName = parent === '__new__' ? newParent.trim() : parent;
  const toggle = (s) => setSelected(sel => (sel.includes(s) ? sel.filter(x => x !== s) : [...sel, s]));
  const changeCat = (slug) => { setCatSlug(slug); setSelected([]); setParent(''); setNewParent(''); setResult(null); };

  const apply = async () => {
    if (!cat || !parentName || !selected.length) return;
    if (!window.confirm(`Regrouper ${selected.length} sous-catégorie(s) sous « ${parentName} » dans ${cat.name} ? Leurs pages passent en URL /pieces/${cat.slug}/${parentName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}/… (l'ancienne URL redirige), les produits restent affectés.`)) return;
    setBusy(true); setResult(null);
    try {
      const c = await categoriesAPI.groupSubcategories(cat.id, parentName, selected);
      setResult({ ok: true, text: `${selected.length} sous-catégorie(s) regroupée(s) sous « ${parentName} » · ${Object.keys(c.subcategory_parents || {}).length} sous-sous-catégorie(s) dans ${c.name}` });
      setSelected([]); setParent(''); setNewParent('');
      onApplied?.(c);
    } catch (e) { setResult({ ok: false, text: e.response?.data?.detail || 'Regroupement impossible' }); } finally { setBusy(false); }
  };

  return (
    <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-4 space-y-3" data-testid="group-subcategories-block">
      <div>
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><FolderTree size={16} className="text-sky-600" /> Migration guidée vers un 3e niveau</p>
        <p className="text-xs text-gray-600">Cochez des sous-catégories existantes et regroupez-les d'un clic sous un parent (sous-catégorie existante ou nouvelle). Elles deviennent des sous-sous-catégories : image, SEO et traductions conservés.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-xs text-gray-600">Catégorie
          <select value={catSlug} onChange={e => changeCat(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="group-category-select">
            {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-600">Parent
          <select value={parent} onChange={e => setParent(e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="group-parent-select">
            <option value="">Choisir…</option>
            <option value="__new__">+ Nouvelle sous-catégorie parente</option>
            {parentChoices.map(s => <option key={s} value={s}>{s}{childrenOf(cat, s).length ? ` (${childrenOf(cat, s).length} enfant(s))` : ''}</option>)}
          </select>
        </label>
        {parent === '__new__' && (
          <label className="text-xs text-gray-600">Nom du nouveau parent
            <Input value={newParent} onChange={e => setNewParent(e.target.value)} placeholder="Ex : Disques de frein" className="mt-1" data-testid="group-new-parent-input" />
          </label>
        )}
      </div>
      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white p-2 flex flex-wrap gap-1.5" data-testid="group-candidates">
        {candidates.length === 0 && <p className="text-xs text-gray-400">Aucune sous-catégorie de niveau 2 regroupable dans cette catégorie.</p>}
        {candidates.filter(s => s !== parentName).map(s => (
          <label key={s} className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-2 py-1 cursor-pointer ${selected.includes(s) ? 'bg-sky-100 border-sky-400 text-sky-900' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-sky-300'}`} data-testid={`group-candidate-${s}`}>
            <input type="checkbox" className="accent-sky-600" checked={selected.includes(s)} onChange={() => toggle(s)} /> {s}{parentOf(cat, s) ? ` (${parentOf(cat, s)})` : ''}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-600" data-testid="group-selected-count">{selected.length} sélectionnée(s){parentName ? ` → « ${parentName} »` : ''}</span>
        <Button onClick={apply} disabled={busy || !parentName || parentName.length < 2 || !selected.length} className="bg-sky-600 hover:bg-sky-700" data-testid="group-apply-button">
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FolderTree size={14} className="mr-1" />} Regrouper
        </Button>
      </div>
      {result && <p className={`text-xs flex items-center gap-1.5 ${result.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="group-result">{result.ok ? <CheckCircle size={13} /> : <AlertTriangle size={13} />} {result.text}</p>}
    </div>
  );
};

export default GroupSubcategoriesBlock;
